import { uploadDocAPI } from 'services/workspaceAPI/';
import { Dispatch, ApplicationStateGetter } from 'store';
import { addToast } from 'store/ui/actions';
import { windowUnloadManager } from 'utils/events/windowUnloadManager';
import { getOrderDocuments } from '.';
import {
  OrderTypeKeys,
  IStagedDocumentUpload,
  IOrderClearActiveUploadsAction,
  IDocumentUpload,
  IOrderStageDocumentsAction,
  IOrderClearStagedDocumentsAction,
} from '../types';
import { createFormData } from 'utils/orderDocuments';
import { isInternalUser } from 'store/auth/getters';
import { DOCUMENT_TYPE } from 'components/OrderDocuments/constants';
import {
  executeCustomerDocumentUpload,
  executeDepositUpload,
  executeDocumentUpload,
} from './DocumentUpload/uploadActions';

const uploadUnloadKey = 'UPLOAD_IN_PROGRESS';
const uploadUnloadHandler = () => 'One or more uploads are in progress';

export const clearActiveDocumentUploads = (): IOrderClearActiveUploadsAction => ({
  type: OrderTypeKeys.UI_CLEAR_ACTIVE_UPLOAD_DOCUMENTS,
});

export const clearStagedDocumentUploads = (): IOrderClearStagedDocumentsAction => ({
  type: OrderTypeKeys.UI_CLEAR_STAGED_DOCUMENT_UPLOADS,
});

export const stageDocumentUploads = (
  documentsToStage: IStagedDocumentUpload[]
): IOrderStageDocumentsAction => ({
  type: OrderTypeKeys.UI_STAGE_DOCUMENT_UPLOADS,
  documentsToStage,
});

export const executeDocumentUploads = async (
  dispatch: Dispatch,
  getState: ApplicationStateGetter
): Promise<Set<string>> => {
  const affectedOrders = new Set<string>();
  let error;
  const uploadQueue = getState().orders.ui.multiDocumentsUpload
    .inProgressUploads;
  const validInternalUser = isInternalUser(getState().auth);
  const userDetails = getState().auth.session;

  const uniqueUploadsQueue = uploadQueue.filter((doc) => doc.progress === 0);

  const promises = uniqueUploadsQueue.map((file) => {
    const uploadHandler = getUploadHandler(
      file,
      validInternalUser,
      userDetails
    );
    return handleDocumentUploadResponse(
      uploadHandler,
      dispatch,
      file,
      affectedOrders,
      error
    );
  });

  await Promise.all(promises);
  return affectedOrders;
};

export const getUploadHandler = (
  file: IDocumentUpload,
  validInternalUser: boolean,
  userDetails: any
) => {
  if (!validInternalUser) {
    return executeCustomerDocumentUpload(file, file.file.name);
  } else if (file.metadata.depositId) {
    return executeDepositUpload(file, file.file.name);
  } else if (file.metadata.dataExtraction && validInternalUser) {
    return async (dispatch: Dispatch) => {
      const res = await dispatch(executeDocumentUpload(file, file.file.name));
      await dispatch(extractDocument(res, userDetails.email));
    };
  } else {
    return executeDocumentUpload(file, file.file.name);
  }
};

export const handleDocumentUploadResponse = async (
  executeUpload: any,
  dispatch: Dispatch,
  file: IDocumentUpload,
  affectedOrders: Set<string>,
  error?: Error
) => {
  try {
    await dispatch(executeUpload);
    dispatch({
      error,
      type: OrderTypeKeys.UI_COMPLETE_ACTIVE_DOCUMENT_UPLOAD,
      fileName: file.file.name,
    });
    affectedOrders.add(file.fileID);
  } catch (error) {
    dispatch({
      error,
      type: OrderTypeKeys.UI_COMPLETE_ACTIVE_DOCUMENT_UPLOAD,
      fileName: file.file.name,
    });
  }
};

export const uploadStagedDocuments = (
  stagedDocuments: IStagedDocumentUpload[]
) => {
  return async (dispatch: Dispatch, getState: ApplicationStateGetter) => {
    dispatch({
      type: OrderTypeKeys.UI_QUEUE_ORDER_DOCUMENT_UPLOAD,
      stagedDocuments,
    });

    windowUnloadManager.registerHandler(uploadUnloadKey, uploadUnloadHandler);
    const affectedOrders = await executeDocumentUploads(dispatch, getState);
    windowUnloadManager.unregisterHandler(uploadUnloadKey);

    const currentOrder = window.location.pathname.match(
      new RegExp('/order/([0-9]+)/documents/?$')
    )?.[1];

    if (currentOrder && affectedOrders.has(currentOrder)) {
      dispatch(getOrderDocuments(currentOrder));
    }
  };
};

export const extractDocument = (upload, createdBy) => {
  const { fileID, documentId, file, metadata } = upload;
  const selectedClassType = DOCUMENT_TYPE.find((docType) => {
    return docType.value === metadata.idpDocumentType;
  });

  const formData = createFormData({
    documentId,
    classType: selectedClassType?.id ?? '',
    file: file,
    createdBy,
  });

  return async (dispatch) => {
    try {
      await uploadDocAPI({ 'Content-Type': 'multipart/form-data' }).post(
        `/orders/${fileID}/document-extractions`,
        formData
      );
      dispatch(
        addToast(
          `Data extraction process for ${metadata.displayName} has started. We'll notify you when it has completed.`,
          'info'
        )
      );
      return true;
    } catch (error) {
      dispatch(
        addToast(
          `Extraction failed to start for ${metadata.displayName}`,
          'error'
        )
      );
      return false;
    }
  };
};

export const startExtractDocument = async (
  orderId: string,
  documentId: string,
  dispatch: Dispatch,
  displayName?: string
): Promise<boolean> => {
  const extractionData = {
    fileID: orderId,
    documentId: documentId,
    file: null,
    metadata: {
      idpDocumentType: DOCUMENT_TYPE[0].value, // Default to the first document type
      displayName: displayName,
    },
  };

  return await dispatch(extractDocument(extractionData, ''));
};
